import { useState } from "react";
import { useFilters } from "@/components/FilterBar";
import { QueryPanel } from "@/components/States";
import { Button, Card, Dialog, Tag } from "@/components/ui";
import { useEvents } from "@/lib/api/queries";
import { formatDateTime, formatLatency, formatNumber, formatUsd } from "@/lib/format";
import type { UsageEvent } from "@/types/api";

function Detail({ event, onClose }: { event: UsageEvent; onClose: () => void }) {
  const rows: Array<[string, string]> = [
    ["event_id", event.event_id],
    ["서비스", event.service_id],
    ["환경", event.environment],
    ["프로바이더 / 모델", `${event.provider} / ${event.model}`],
    ["오퍼레이션", event.operation],
    ["토큰", `입력 ${formatNumber(event.prompt_tokens)} · 출력 ${formatNumber(event.completion_tokens)} · 합계 ${formatNumber(event.total_tokens)}`],
    ["비용", formatUsd(event.cost_usd)],
    ["지연", formatLatency(event.latency_ms)],
    ["상태", event.status + (event.error_type ? ` (${event.error_type})` : "")],
    ["request_id", event.request_id ?? "-"],
    ["trace_id", event.trace_id ?? "-"],
    ["user_ref (해시)", event.user_ref ?? "-"],
    ["발생 시각", formatDateTime(event.occurred_at)],
    ["수집 시각", formatDateTime(event.received_at)],
  ];

  return (
    <Dialog open onClose={onClose} title="이벤트 상세">
      <div className="flex flex-col gap-4">
        <dl className="grid grid-cols-[minmax(120px,auto)_1fr] gap-x-4 gap-y-2 text-label-2">
          {rows.map(([label, content]) => (
            <div key={label} className="contents">
              <dt className="text-label-alternative">{label}</dt>
              <dd className="break-all text-label-normal tabular-nums">{content}</dd>
            </div>
          ))}
        </dl>

        <div>
          <h3 className="mb-2 text-label-1 font-semibold text-label-strong">커스텀 필드</h3>
          {Object.keys(event.metadata).length === 0 ? (
            <p className="text-label-2 text-label-assistive">없어요.</p>
          ) : (
            <pre className="max-h-64 overflow-auto rounded-sm bg-background-alternative p-3 text-caption-1 text-label-normal">
              {JSON.stringify(event.metadata, null, 2)}
            </pre>
          )}
        </div>

        <p className="text-caption-1 text-label-assistive">
          프롬프트·응답 본문은 수집하지 않아요. user_ref 는 서버에서 솔트를 섞어 해싱한 값이에요.
        </p>
      </div>
    </Dialog>
  );
}

export function Logs() {
  const [filters] = useFilters();
  // Cursor pagination: the API hands back an opaque keyset token, and we keep a
  // stack of the ones we've used so "이전" is possible without offsets.
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<UsageEvent | null>(null);

  const query = useEvents(filters, cursors[page] ?? null);

  // A filter change invalidates the cursor stack — reset to the first page.
  const filterKey = JSON.stringify(filters);
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) {
    setLastFilterKey(filterKey);
    setCursors([null]);
    setPage(0);
  }

  function next() {
    const cursor = query.data?.next_cursor;
    if (!cursor) return;
    setCursors((stack) => (page + 1 < stack.length ? stack : [...stack, cursor]));
    setPage((value) => value + 1);
  }

  return (
    <Card>
      <QueryPanel query={query} isEmpty={(data) => data.items.length === 0 && page === 0}>
        {(data) => (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse text-label-2">
                <thead>
                  <tr className="border-b border-line-neutral text-left text-label-alternative">
                    <th scope="col" className="px-3 py-2 font-semibold">시각</th>
                    <th scope="col" className="px-3 py-2 font-semibold">서비스</th>
                    <th scope="col" className="px-3 py-2 font-semibold">모델</th>
                    <th scope="col" className="px-3 py-2 text-right font-semibold">토큰</th>
                    <th scope="col" className="px-3 py-2 text-right font-semibold">비용</th>
                    <th scope="col" className="px-3 py-2 text-right font-semibold">지연</th>
                    <th scope="col" className="px-3 py-2 font-semibold">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((event) => (
                    <tr
                      key={event.event_id}
                      onClick={() => setSelected(event)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelected(event);
                        }
                      }}
                      tabIndex={0}
                      className="cursor-pointer border-b border-line-alternative last:border-0 hover:bg-fill-alternative focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
                    >
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-label-alternative">
                        {formatDateTime(event.occurred_at)}
                      </td>
                      <td className="px-3 py-2 text-label-normal">{event.service_id}</td>
                      <td className="px-3 py-2 text-label-normal">
                        <span className="text-label-assistive">{event.provider}/</span>
                        {event.model}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(event.total_tokens)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatUsd(event.cost_usd)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatLatency(event.latency_ms)}</td>
                      <td className="px-3 py-2">
                        <Tag size="small" color={event.status === "error" ? "negative" : "positive"}>
                          {event.status === "error" ? (event.error_type ?? "error") : "success"}
                        </Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-caption-1 text-label-assistive tabular-nums">
                {page + 1}페이지 · {data.items.length}건
              </span>
              <div className="flex gap-2">
                <Button
                  size="small"
                  variant="outlined"
                  color="assistive"
                  iconLeft="chevronLeft"
                  disabled={page === 0}
                  onClick={() => setPage((value) => Math.max(0, value - 1))}
                >
                  이전
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="assistive"
                  iconRight="chevronRight"
                  disabled={!data.next_cursor}
                  onClick={next}
                >
                  다음
                </Button>
              </div>
            </div>
          </>
        )}
      </QueryPanel>

      {selected && <Detail event={selected} onClose={() => setSelected(null)} />}
    </Card>
  );
}
